import axios from "axios";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  CircularProgress,
  DialogContent,
  Dialog,
  DialogTitle,
  Typography,
  Button,
  DialogActions,
  Tooltip,
} from "@mui/material";
import {
  DownloadForOffline,
  Preview,
  Assignment,
  HourglassBottom,
  DisabledByDefault,
  ThumbUpAltOutlined,
  CheckCircleOutline,
  ThumbDownAltOutlined,
  CancelOutlined,
} from "@mui/icons-material";
import VerifiedIcon from "@mui/icons-material/Verified";
import SignaturePad from "./SignaturePad";
import classes from "./Invoice.module.css";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import { useVendorInvoiceDetailsOffline } from "../../../offline";

const VendorInvoice = () => {
  const loginData = useSelector((state) => state.loginData.value);

  const [user_token] = useState(loginData?.user?.user_token);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [signingInvoice, setSigningInvoice] = useState(null);
  const [signedInvoices, setSignedInvoices] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);

  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [enteredOTP, setEnteredOTP] = useState("");
  const [currentInvoiceToken, setCurrentInvoiceToken] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const authToken = loginData?.access_token || loginData?.token || "";
  const {
    invoices: vendorinvoicedata,
    loading: isLoading,
    totalCount,
    refetch: refetchInvoices,
  } = useVendorInvoiceDetailsOffline(authToken, user_token, page + 1);

  const downloadPDF = (pdfBase64) => {
    if (!pdfBase64) {
      setDialogMessage("Invoice PDF not available");
      setDialogOpen(true);
      return;
    }
    const byteChars = atob(pdfBase64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Invoice.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openPDFInNewTab = (invoice) => {
    if (!invoice?.pdf) {
      setDialogMessage("Invoice PDF not available");
      setDialogOpen(true);
      return;
    }

    const byteChars = atob(invoice.pdf);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const handleApprove = async (invoice_token, action) => {
    try {
      setButtonLoading(true);
      await axios.post(API_ENDPOINT.approve_vendor_invoice, {
        invoice_token,
        action,
      });

      setDialogMessage(
        `Invoice ${action === "accept" ? "Accepted" : "Rejected"}!`
      );
      setDialogOpen(true);
      refetchInvoices();
    } catch (err) {
      setDialogMessage("Failed to process invoice.");
      setDialogOpen(true);
    } finally {
      setButtonLoading(false);
    }
  };

  const handleSaveSignature = async (base64Image) => {
    if (!signingInvoice) return;

    setDialogOpen(true);
    try {
      await axios.post(API_ENDPOINT.sign_invoice, {
        invoice_token: signingInvoice.invoice_token,
        signature: base64Image,
      });

      setSignedInvoices((prev) => ({
        ...prev,
        [signingInvoice.invoice_token]: true,
      }));

      setDialogMessage("Signature embedded successfully!");
      setDialogOpen(true);

      setIsModalOpen(false);
      setSigningInvoice(null);
      await refetchInvoices();
    } catch (err) {
      console.error(err);
      setDialogMessage("Failed to embed signature.");
      setDialogOpen(true);
    } finally {
      setDialogOpen(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOTPSubmit = async () => {
    setDialogOpen(true);
    try {
      await axios.post(API_ENDPOINT.sign_invoice, {
        invoice_token: currentInvoiceToken,
        otp: enteredOTP,
        approval_type: "otp",
      });

      setDialogMessage("Invoice approved via OTP!");
      setOtpDialogOpen(false);
      setEnteredOTP("");
      await refetchInvoices();
    } catch (err) {
      console.error(err);
      setDialogMessage("OTP verification failed. Try again.");
    } finally {
      setDialogOpen(true);
    }
  };

  const triggerOTP = (item) => {
    // TODO: implement OTP flow if needed
  };

  const sortedData = useMemo(() => {
    const list = Array.isArray(vendorinvoicedata) ? vendorinvoicedata : [];
    return [...list].sort(
      (a, b) => new Date(b.created_on) - new Date(a.created_on)
    );
  }, [vendorinvoicedata]);

  const filteredData = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();
    return sortedData.filter((item) => {
      const matchesSearch =
        !q ||
        item.vendor_name?.toLowerCase().includes(q) ||
        item.amount?.toString().includes(q);

      const matchesStatus =
        statusFilter === "All" || item.status_label === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [sortedData, searchText, statusFilter]);

  const pagedData = filteredData;

  return (
    <div className={classes.conatiner}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <HeaderTitle titleText={"Merchant Invoice"} />

        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          {/* Status Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "10px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              outline: "none",
              width: "150px",
              fontFamily: "GilroyMedium",
            }}
          >
            <option value="All">All</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Awaiting</option>
            <option value="Cancelled">Rejected</option>
          </select>

          {/* Search Box */}
          {/* <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: "10px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              width: "250px",
              outline: "none",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#1976d2")}
            onBlur={(e) => (e.target.style.borderColor = "#ccc")}
          /> */}

          <SearchBar  value={searchText} onChange={setSearchText}/>
        </div>
      </div>

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer style={{ background: "#fff" }}>
          <Table
            sx={{
              "& .MuiTableCell-head": {
                fontFamily: "GilroyBold !important",
                backgroundColor: "#e0e9ee",
              },
              "& .MuiTableCell-body": {
                fontFamily: "GilroyMedium !important",
              },
              "& .MuiTableRow-root:hover td": {
                backgroundColor: "rgba(224,233,238,0.35)",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell style={{ fontSize: "15px" }}>
                  Supplier Name
                </TableCell>
                <TableCell style={{ fontSize: "15px" }}>
                  Amount(₹)
                </TableCell>
                <TableCell style={{ fontSize: "15px" }}>
                  Created Date
                </TableCell>
                <TableCell style={{ fontSize: "15px" }}>
                  Invoice
                </TableCell>
                <TableCell style={{ fontSize: "15px" }}>
                  Action
                </TableCell>
                <TableCell style={{ fontSize: "15px" }}>
                  Status
                </TableCell>
                <TableCell style={{ fontSize: "15px" }}>
                  Approve With
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedData.length > 0 ? (
                pagedData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell style={{ fontSize: "14px" }}>
                      {item.vendor_name}
                    </TableCell>
                    <TableCell style={{ fontSize: "14px" }}>
                      ₹ {Number(item.amount || 0).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell style={{ fontSize: "14px" }}>
                      {new Date(item.created_on).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <DownloadForOffline
                        fontSize="medium"
                        style={{ cursor: "pointer", fontSize: "23px" }}
                        onClick={() => downloadPDF(item.pdf)}
                        color="primary"
                      />
                      <Preview
                        fontSize="medium"
                        style={{ cursor: "pointer", fontSize: "23px" }}
                        onClick={() => openPDFInNewTab(item)}
                        color="action"
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "5px",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {item.is_bizfin_approved == 1 ? (
                          <>
                            {item.status_label !== "Cancelled" && (
                              <Tooltip
                                title={
                                  item.signed !== "Signed"
                                    ? "Please sign the invoice first"
                                    : ""
                                }
                                classes={{ tooltip: classes.gilroyTooltip }}
                              >
                                <span>
                                  <Button
                                    variant="contained"
                                    onClick={() =>
                                      handleApprove(item.invoice_token, "accept")
                                    }
                                    disabled={
                                      item.status_label === "Approved" ||
                                      item.signed !== "Signed"
                                    }
                                    startIcon={<CheckCircleOutline />}
                                    sx={{
                                      fontFamily: "GilroyBold",
                                      fontSize:"13px",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {item.status_label === "Approved"
                                      ? "Approved"
                                      : "Approve Request"}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}

                            {item.status_label !== "Approved" && (
                              <Button
                                variant="outlined"
                                onClick={() =>
                                  handleApprove(item.invoice_token, "reject")
                                }
                                disabled={item.status_label === "Cancelled"}
                                startIcon={
                                  item.status_label === "Cancelled" ? (
                                    <ThumbDownAltOutlined />
                                  ) : (
                                    <CancelOutlined />
                                  )
                                }
                                sx={{
                                  fontFamily: "GilroyBold",
                                  fontSize:"13px",
                                  textTransform: "uppercase",
                                }}
                              >
                                {item.status_label === "Cancelled"
                                  ? "Rejected"
                                  : "Reject Request"}
                              </Button>
                            )}
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            disabled
                            sx={{
                              color: "#b0b0b0 !important",
                              borderColor: "#ddd !important",
                              fontSize: "12px",
                              textTransform: "none",
                              padding: "4px 12px",
                            }}
                          >
                            No Action Required
                          </Button>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      {item.status_label === "Approved" ? (
                        <span
                          style={{
                            color: "green",
                            
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <VerifiedIcon fontSize="medium" /> Approved
                        </span>
                      ) : item.status_label === "Pending" ? (
                        <span
                          style={{
                            color: "orange",
                            
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <HourglassBottom /> Awaiting
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "red",
                            
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "14px",
                          }}
                        >
                          <DisabledByDefault /> Rejected
                        </span>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "middle" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "nowrap",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {/* APPROVE WITH OTP BUTTON */}
                        <Button
                          variant="contained"
                          onClick={() => triggerOTP(item)}
                          startIcon={<ThumbUpAltOutlined />}
                          size="small"
                          disableElevation
                          sx={{
                            fontFamily: "GilroyBold",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            minWidth: 170,
                            fontSize: "12px",
                            lineHeight: 1.1,
                            px: 1.5,
                            py: 0.8,
                          }}
                        >
                          Approve with OTP
                        </Button>

                        {/* DIGITAL SIGN BUTTON (Only when approved but not signed yet) */}
                        {item.is_bizfin_approved === 1 &&
                          item.signed !== "Signed" && (
                            <Assignment
                              onClick={() => {
                                if (item.status !== 1) {
                                  setIsModalOpen(true);
                                  setSigningInvoice(item);
                                }
                              }}
                              style={{
                                cursor:
                                  item.status === 1 ? "not-allowed" : "pointer",
                                opacity: item.status === 1 ? 0.4 : 1,
                                fontSize: "24px",
                                marginLeft: "8px",
                              }}
                              color={
                                item.status === 1 || item.status === 3
                                  ? "disabled"
                                  : "secondary"
                              }
                            />
                          )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{
                      py: 4,
                      fontSize: "14px",
                      fontFamily: "GilroyMedium",
                      color: "#5f6b7a",
                    }}
                  >
                    No data found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5]}
            component="div"
            count={Number(totalCount || 0)}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": { fontSize: "14px" },
              "& .MuiTablePagination-selectLabel": { fontSize: "14px" },
              "& .MuiTablePagination-select": { fontSize: "14px" },
              "& .MuiTablePagination-displayedRows": { fontSize: "14px" },
            }}
          />
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: 300,
            bgcolor: "background.paper",
            p: 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "20px", fontWeight: 600 ,fontFamily:"gilroyBold"}}>
          Notification
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "16px" ,fontFamily:"gilroyMedium"}}>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        PaperProps={{
          sx: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: 300,
            bgcolor: "background.paper",
            p: 2,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "18px", fontWeight: 600 }}>
          Enter OTP
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", mb: 1 }}>
            OTP has been sent to your registered contact.
          </Typography>
          <input
            type="number"
            value={enteredOTP}
            onChange={(e) => setEnteredOTP(e.target.value)}
            placeholder="Enter OTP"
            style={{
              width: "100%",
              padding: "8px",
              fontSize: "16px",
              marginBottom: "10px",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOtpDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleOTPSubmit}
            disabled={!enteredOTP}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <SignaturePad
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveSignature}
      />
    </div>
  );
};

export default VendorInvoice;
